'use client';

import { useEffect, useState, useCallback } from 'react';
import Link from 'next/link';
import { apiGet } from '@/lib/apiClient';

interface Stats {
  nowe: number;
  weryfikacja: number;
  wRealizacji: number;
  oczekujeZewn: number;
  zamknieteToday: number;
  nieprzypisane: number;
  slaOverdue: number;
}

interface RecentTicket {
  id: string;
  ticketNumber: string;
  status: string;
  priority: string;
  categoryName: string | null;
  addressText: string | null;
  createdAt: string;
}

const STATUS_COLORS: Record<string, { bg: string; text: string; dot: string }> = {
  NOWE:          { bg: 'bg-amber-50',   text: 'text-amber-700',   dot: 'bg-amber-400' },
  WERYFIKACJA:   { bg: 'bg-blue-50',    text: 'text-blue-700',    dot: 'bg-blue-500' },
  W_REALIZACJI:  { bg: 'bg-violet-50',  text: 'text-violet-700',  dot: 'bg-violet-500' },
  OCZEKUJE_ZEWN: { bg: 'bg-orange-50',  text: 'text-orange-700',  dot: 'bg-orange-400' },
  ZAMKNIETE:     { bg: 'bg-emerald-50', text: 'text-emerald-700', dot: 'bg-emerald-500' },
  ODRZUCONE:     { bg: 'bg-red-50',     text: 'text-red-700',     dot: 'bg-red-500' },
};

const STATUS_LABELS: Record<string, string> = {
  NOWE: 'Nowe', WERYFIKACJA: 'Weryfikacja', W_REALIZACJI: 'W realizacji',
  OCZEKUJE_ZEWN: 'Oczekuje zewn.', ZAMKNIETE: 'Zamknięte', ODRZUCONE: 'Odrzucone',
};

function StatusBadge({ status }: { status: string }) {
  const s = STATUS_COLORS[status] ?? { bg: 'bg-gray-50', text: 'text-gray-600', dot: 'bg-gray-400' };
  return (
    <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-semibold ${s.bg} ${s.text}`}>
      <span className={`w-1.5 h-1.5 rounded-full ${s.dot}`} />
      {STATUS_LABELS[status] ?? status}
    </span>
  );
}

function fmtDateTime(iso: string) {
  return new Date(iso).toLocaleString('pl-PL', {
    day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit',
  });
}

export default function DashboardPage() {
  const [stats, setStats] = useState<Stats | null>(null);
  const [recent, setRecent] = useState<RecentTicket[]>([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      // Pobierz liczniki statusów równolegle
      const [nowe, wer, real, ocz, niezam, nieprzyp, sla] = await Promise.all([
        apiGet<{ count: number }>('/tickets/count?status=NOWE'),
        apiGet<{ count: number }>('/tickets/count?status=WERYFIKACJA'),
        apiGet<{ count: number }>('/tickets/count?status=W_REALIZACJI'),
        apiGet<{ count: number }>('/tickets/count?status=OCZEKUJE_ZEWN'),
        apiGet<{ count: number }>('/tickets/count?status=ZAMKNIETE&closedToday=true'),
        apiGet<{ count: number }>('/tickets/count?unassigned=true'),
        apiGet<{ count: number }>('/tickets/count?slaOverdue=true'),
      ]);

      setStats({
        nowe: nowe.count ?? 0,
        weryfikacja: wer.count ?? 0,
        wRealizacji: real.count ?? 0,
        oczekujeZewn: ocz.count ?? 0,
        zamknieteToday: niezam.count ?? 0,
        nieprzypisane: nieprzyp.count ?? 0,
        slaOverdue: sla.count ?? 0,
      });

      // Pobierz ostatnie 8 zgłoszeń
      const ticketsRes = await apiGet<{ data: RecentTicket[] }>('/tickets?limit=8&sortBy=createdAt&sortOrder=desc');
      setRecent(ticketsRes.data ?? []);
    } catch {
      // silent — API może być niedostępne
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { void load(); }, [load]);

  const statCards = stats ? [
    {
      label: 'Nowe zgłoszenia',
      value: stats.nowe,
      icon: '📬',
      bg: 'bg-amber-50',
      text: 'text-amber-600',
      border: 'border-amber-200',
      href: '/panel/tickets?status=NOWE',
    },
    {
      label: 'W realizacji',
      value: stats.wRealizacji,
      icon: '⚙️',
      bg: 'bg-violet-50',
      text: 'text-violet-600',
      border: 'border-violet-200',
      href: '/panel/tickets?status=W_REALIZACJI',
    },
    {
      label: 'Nieprzypisane',
      value: stats.nieprzypisane,
      icon: '👤',
      bg: 'bg-blue-50',
      text: 'text-blue-600',
      border: 'border-blue-200',
      href: '/panel/tickets?unassigned=true',
    },
    {
      label: 'Po terminie (SLA)',
      value: stats.slaOverdue,
      icon: '⏰',
      bg: 'bg-red-50',
      text: 'text-red-600',
      border: 'border-red-200',
      href: '/panel/tickets?sortBy=slaResponseAt&sortOrder=asc',
    },
    {
      label: 'Zamknięte dziś',
      value: stats.zamknieteToday,
      icon: '✅',
      bg: 'bg-emerald-50',
      text: 'text-emerald-600',
      border: 'border-emerald-200',
      href: '/panel/tickets?status=ZAMKNIETE',
    },
    {
      label: 'Weryfikacja',
      value: stats.weryfikacja,
      icon: '🔍',
      bg: 'bg-sky-50',
      text: 'text-sky-600',
      border: 'border-sky-200',
      href: '/panel/tickets?status=WERYFIKACJA',
    },
    {
      label: 'Oczekuje zewn.',
      value: stats.oczekujeZewn,
      icon: '🕒',
      bg: 'bg-orange-50',
      text: 'text-orange-600',
      border: 'border-orange-200',
      href: '/panel/tickets?status=OCZEKUJE_ZEWN',
    },
  ] : [];

  return (
    <div className="max-w-6xl mx-auto">
      {/* Nagłówek */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Dashboard</h1>
        <p className="text-sm text-gray-500 mt-1">Przegląd aktywności systemu zgłoszeń</p>
      </div>

      {/* Karty statystyk */}
      {loading ? (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 mb-6">
          {Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className="bg-white rounded-xl border border-gray-200 p-4 animate-pulse">
              <div className="w-8 h-8 bg-gray-200 rounded-lg mb-3" />
              <div className="w-12 h-7 bg-gray-200 rounded mb-1" />
              <div className="w-20 h-3 bg-gray-100 rounded" />
            </div>
          ))}
        </div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 mb-6">
          {statCards.map((s) => (
            <Link
              key={s.label}
              href={s.href}
              className={`bg-white rounded-xl border ${s.border} p-4 hover:shadow-md transition-shadow group`}
            >
              <div className={`w-9 h-9 ${s.bg} rounded-lg flex items-center justify-center text-lg mb-3`}>
                {s.icon}
              </div>
              <div className={`text-2xl font-bold ${s.text} group-hover:scale-105 transition-transform`}>
                {s.value}
              </div>
              <div className="text-xs text-gray-400 mt-0.5 leading-tight">{s.label}</div>
            </Link>
          ))}
        </div>
      )}

      {/* Szybkie akcje */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        {[
          { href: '/panel/tickets', label: 'Lista zgłoszeń', icon: '📋', desc: 'Zarządzaj zgłoszeniami' },
          { href: '/panel/map',     label: 'Mapa',            icon: '🗺',  desc: 'Widok geograficzny' },
          { href: '/panel/clusters',label: 'Sprawy zbiorcze', icon: '📁',  desc: 'Grupuj zgłoszenia' },
          { href: '/panel/settings/users', label: 'Użytkownicy', icon: '👥', desc: 'Zarządzaj zespołem' },
        ].map((a) => (
          <Link
            key={a.href}
            href={a.href}
            className="bg-white rounded-xl border border-gray-200 px-4 py-3 flex items-center gap-3 hover:border-indigo-300 hover:shadow-sm transition-all group"
          >
            <span className="text-xl">{a.icon}</span>
            <div>
              <p className="text-sm font-semibold text-gray-800 group-hover:text-indigo-700 transition-colors">{a.label}</p>
              <p className="text-xs text-gray-400">{a.desc}</p>
            </div>
          </Link>
        ))}
      </div>

      {/* Ostatnie zgłoszenia */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <div className="flex items-center justify-between px-5 py-3.5 border-b border-gray-100">
          <h2 className="text-sm font-semibold text-gray-800">Ostatnie zgłoszenia</h2>
          <Link href="/panel/tickets" className="text-xs text-indigo-600 hover:underline font-medium">
            Zobacz wszystkie →
          </Link>
        </div>

        {loading ? (
          <div className="divide-y divide-gray-50">
            {Array.from({ length: 5 }).map((_, i) => (
              <div key={i} className="px-5 py-3 flex items-center gap-3 animate-pulse">
                <div className="w-24 h-4 bg-gray-200 rounded" />
                <div className="w-16 h-5 bg-gray-100 rounded-full" />
                <div className="flex-1 h-4 bg-gray-100 rounded" />
                <div className="w-20 h-3 bg-gray-100 rounded" />
              </div>
            ))}
          </div>
        ) : recent.length === 0 ? (
          <div className="py-12 text-center text-gray-400">
            <div className="text-4xl mb-2">📭</div>
            <p className="text-sm">Brak zgłoszeń do wyświetlenia</p>
          </div>
        ) : (
          <div className="divide-y divide-gray-50">
            {recent.map((t) => (
              <Link
                key={t.id}
                href={`/panel/tickets/${t.id}`}
                className="flex items-center gap-3 px-5 py-3 hover:bg-gray-50 transition-colors group"
              >
                <span className="font-mono text-sm font-semibold text-indigo-600 shrink-0 group-hover:underline">
                  #{t.ticketNumber}
                </span>
                <StatusBadge status={t.status} />
                <span className="flex-1 text-sm text-gray-700 truncate min-w-0">
                  {t.categoryName ? `${t.categoryName} — ` : ''}{t.addressText ?? '—'}
                </span>
                <span className="text-xs text-gray-400 shrink-0">{fmtDateTime(t.createdAt)}</span>
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
